import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Target, Swords, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Bits";
import { signed } from "@/lib/share";

function StepLeague({ lg, members }) {
  const r = lg?.scoring_rule || {};
  return (
    <div className="space-y-3 text-sm text-slate-300">
      <p className="[overflow-wrap:anywhere]">Tu fais partie de <b className="text-white">{lg?.name}</b>, une ligue privée.</p>
      <div className="flex flex-wrap gap-1.5">{members.slice(0, 10).map((m) => <span key={m.id} className="flex items-center gap-1.5 rounded-full border border-white/10 px-2 py-1 text-xs"><Avatar user={m} size={18} /> {m.display_name}</span>)}</div>
      <p>Barème <b className="text-white">{r.label}</b> : bon vainqueur <b className="text-[#B7F52A]">{signed(r.correct_points || 0)}</b>, mauvais vainqueur <b className="text-red-300">{signed(r.wrong_points || 0)}</b>. La compétition démarre à 4 membres.</p>
    </div>
  );
}

const STEPS = [
  { icon: Users, title: "Bienvenue dans ta ligue" },
  { icon: Target, title: "Fais ton premier pronostic", body: ["Touche l'équipe que tu vois gagner : ton choix est enregistré tout de suite.", "Ouvre « Pronostics bonus » pour répondre aux questions facultatives (meilleur marqueur, total de points…).", "Tu peux tout modifier jusqu'au début du match ; ensuite c'est verrouillé.", "Une bonne réponse rapporte des points, une mauvaise peut en retirer : les gains et les risques sont affichés avant chaque choix."] },
  { icon: Swords, title: "Entre dans la rivalité", body: ["Le classement se met à jour après chaque match officiel.", "Le fil social permet de commenter, réagir et envoyer des GIFs.", "Les défis sont volontaires : chacun accepte ou refuse, et il existe toujours une option sans dépense."] },
];

export default function WelcomeGuide({ open, onClose, leagueId }) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [lg, setLg] = useState(null);
  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (!open || !leagueId) return;
    setStep(0);
    api.get(`/leagues/${leagueId}`).then(({ data }) => setLg(data)).catch(() => {});
    api.get(`/leagues/${leagueId}/members`).then(({ data }) => setMembers(data)).catch(() => {});
  }, [open, leagueId]);
  const finish = async (setup) => {
    onClose();
    try { const { data } = await api.patch("/me", { onboarding_done: true }); setUser(data); } catch (e) { /* guide stays skippable */ }
    if (setup === true) nav("/configurer?etape=1");
  };
  const S = STEPS[step];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="border-white/10 bg-[#0B1020] text-white sm:max-w-md" data-testid="welcome-guide">
        <p className="mpl-eyebrow text-[#B7F52A]">Étape {step + 1} sur 3</p>
        <DialogTitle className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase"><S.icon size={20} className="text-[#B7F52A]" /> {S.title}</DialogTitle>
        <DialogDescription className="sr-only">Guide de première connexion</DialogDescription>
        {step === 0 ? <StepLeague lg={lg} members={members} /> : <ul className="space-y-2 text-sm text-slate-300">{S.body.map((b) => <li key={b} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B7F52A]" />{b}</li>)}</ul>}
        <div className="mt-2 flex items-center justify-between">
          <button onClick={() => finish()} className="text-xs font-semibold text-slate-400 hover:text-white" data-testid={`welcome-skip-${step + 1}`}>Passer</button>
          {step < 2 ? <button onClick={() => setStep(step + 1)} className="mpl-btn-primary" data-testid="welcome-next">Suivant <ArrowRight size={15} /></button>
            : <button onClick={() => finish(true)} className="mpl-btn-primary" data-testid="welcome-start">Installer et régler mes alertes</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
